import React from 'react';

import Tag from '@hero/ui-kit/components/Tag';
import type { ClassificationDTO } from '@hero/hero-types';

import { DetailsContainer, DetailLabel, Detail } from '../../../../components/Details';
import {
    formatEvidenceValue,
    getConfidenceLabel,
    getConfidenceTagType,
    getDispositionLabel,
    getEvidenceKeyLabel,
    titleCase
} from './labels';
import * as styles from './style.module.css';

type Props = {
    classification: ClassificationDTO | null | undefined;
};

const safeStringify = (value: unknown): string => {
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const ClassificationRow: React.FC<Props> = ({ classification }) => {
    if (!classification || typeof classification !== 'object') {
        return null;
    }
    const { pattern, confidence, disposition, evidence } = classification;
    const evidenceEntries =
        evidence !== null && typeof evidence === 'object' ? Object.entries(evidence) : [];
    const hasEvidence = evidenceEntries.length > 0;

    return (
        <div className={styles.classificationCard}>
            <div className={styles.classificationHeader}>
                <div className={styles.classificationTitle}>
                    <span className={styles.classificationPattern}>
                        {pattern ? titleCase(pattern) : 'Unknown'}
                    </span>
                    {pattern ? <code className={styles.classificationToken}>{pattern}</code> : null}
                </div>
                <Tag type={getConfidenceTagType(confidence)} size="small">
                    {`${getConfidenceLabel(confidence)} confidence`}
                </Tag>
                <span className={styles.classificationDisposition}>
                    → {getDispositionLabel(disposition)}
                </span>
            </div>

            {hasEvidence ? (
                <div>
                    <span className={styles.evidenceLabel}>Evidence</span>
                    <DetailsContainer>
                        {evidenceEntries.map(([key, value]) => (
                            <React.Fragment key={key}>
                                <DetailLabel>{getEvidenceKeyLabel(key)}</DetailLabel>
                                <Detail>{formatEvidenceValue(value)}</Detail>
                            </React.Fragment>
                        ))}
                    </DetailsContainer>
                    <details>
                        <summary className={styles.rawToggle}>Raw JSON</summary>
                        <pre className={styles.evidence}>{safeStringify(evidence)}</pre>
                    </details>
                </div>
            ) : null}
        </div>
    );
};

export default ClassificationRow;
